<script lang="ts">
	//Svelte hooks
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	//Components
	import {
		DeviceButtonV2,
		Icon,
		ParameterCardV2,
		Stack,
		Text,
		ControlsContainer,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from 'src/types/Device';
	import type { DotsPropsTypes } from '@Shelly/shelly-design-system';
	import type { InputReadersEventsType } from 'src/helpers/input_reader_helpers';
	//Helpers
	import { is_gen_2, isBluRotaryKnob } from '../../../helpers/interface_helpers';
	import { supportsRemoteInputTrigger } from 'src/helpers/input_reader_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { inputStates } from 'src/stores/features/input-states';
	import IconForInputReader from '../../../components/EventLog/IconForInputReader.svelte';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import DoubleIconForInputReader from 'src/components/EventLog/DoubleIconForInputReader.svelte';
	import RemoteControlVisualizer from './RemoteControlVisualizer.svelte';
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	let className = '';
	export { className as class };
	export let device: Device;
	export let status: { inputs? };
	export let settings: {
		inputs?;
		sys?: { device?: { fw_id?: string } };
		DeviceInfo?: { ver?: string };
	};
	export let isInCard: boolean = true;
	export let showLabel: boolean = false;

	$: canTriggerInput =
		btnType === 'button' && !!device.virtual_input && supportsRemoteInputTrigger(device, settings);
	$: switchStatusLabel = indicator_status
		? capFirstChar($format('on', { default: 'on' }))
		: capFirstChar($format('off', { default: 'off' }));

	const mappedEventTexts: InputReadersEventsType = {
		S: $format('event_one_click', { default: 'One click' }),
		SS: $format('event_double_click', { default: 'Double click' }),
		SSS: $format('event_triple_click', { default: 'Triple click' }),
		L: $format('event_long_click', { default: 'Long click' }),
		SL: $format('event_short_long_click', { default: 'Short long click' }),
		LS: $format('event_long_short_click', { default: 'Long short click' }),
	};

	const eventTypes = [
		'',
		'single_push',
		'double_push',
		'triple_push',
		'long_push',
		'btn_down',
		'btn_up',
	];

	$: hasRights = hasControlRights(device);

	let inputColor: DotsPropsTypes['color'] = 'primary';
	let lasteventTimestamp = 0;

	let btnType: string, indicator_status: boolean, event: string, multiple_events: Array<any>;

	// Constants for push type handling
	const PUSH_TYPE_RESET = 0;
	const PUSH_TYPE_LONG_PRESS = 4;
	const PUSH_TYPE_MAX = 3;
	const PUSH_TIMEOUT_MS = 500;

	let pushType = PUSH_TYPE_RESET;
	let timer = null;
	let longpress = false;

	// Rotation/scroll wheel data for remote controllers
	let rotationData;
	let buttonEvents = [];

	beforeUpdate(() => {
		const time = +(new Date().getTime() / 1000).toFixed(0);

		if (typeof status?.inputs !== 'undefined') {
			btnType = settings?.inputs?.[device?.channel]?.btn_type;
			indicator_status = status?.inputs?.[device?.channel]?.input === 1;
		} else if (is_gen_2(device)) {
			btnType = settings?.['input:' + device?.channel]?.type;
			indicator_status = status?.['input:' + device?.channel]?.state;
		} else if (device.gen == 'GBLE') {
			indicator_status = status?.['input:' + device?.channel]?.state;
			if (Array.isArray($inputStates?.[device.id])) {
				multiple_events = $inputStates?.[device.id]; //since we are displaying only single click event ill pass all of the object to have the time stamp

				// Separate button events from rotation events
				buttonEvents = [];
				rotationData = undefined;

				multiple_events.forEach((evt) => {
					// Check if this is a rotation event (has steps property)
					// Rotation events come from rotinput:0 status key and always have steps
					if (typeof evt.steps === 'number') {
						// This is rotation data
						rotationData = evt;
					} else {
						// This is a button event (v_eve:)
						buttonEvents.push(evt);
					}
				});
			}
		} else {
			indicator_status = false;
		}

		event = $inputStates?.[device.id]?.event;
		lasteventTimestamp = $inputStates?.[device.id]?.time;

		if (typeof $inputStates?.[device.id] !== 'undefined') {
			if (time - lasteventTimestamp > 10) {
				inputColor = 'default';
				lasteventTimestamp = 0;
			} else {
				inputColor = 'primary';
			}
		}
	});

	const sendInputEvent = async () => {
		if (pushType == PUSH_TYPE_RESET) return;
		const res = await gen2Request(device, 'Input.Trigger', {
			id: device.channel,
			event_type: eventTypes[pushType],
		});
		// if (res.isok) {
		// 	//Todo what to ?? or nothing
		// } else {
		// }
	};

	const handleLongPressClick = () => {
		pushType = PUSH_TYPE_LONG_PRESS;
		sendInputEvent();
		pushType = PUSH_TYPE_RESET;
		longpress = false;
	};

	const handleNormalClick = () => {
		pushType++;
		clearTimeout(timer);
		timer = setTimeout(() => {
			if (pushType > PUSH_TYPE_MAX) {
				pushType = PUSH_TYPE_MAX;
			}
			sendInputEvent();
			pushType = PUSH_TYPE_RESET;
		}, PUSH_TIMEOUT_MS);
	};

	const handleClick = (e: Event) => {
		if (longpress) {
			handleLongPressClick();
		} else {
			handleNormalClick();
		}

		e.preventDefault();
		e.stopPropagation();
	};

	const handleTouchLongpress = (e: Event) => {
		longpress = true;
		e.preventDefault();
		e.stopPropagation();
	};

	const handleTouchStart = (e: Event) => {
		handleClick(e);
	};
</script>

{#if btnType === 'toggle' || btnType === 'switch'}
	<ParameterCardV2
		icon={mapIconToThin('fas fa-power-off')}
		title={switchStatusLabel}
		size="m"
		backgroundColor="--color-0"
	/>
{:else if isInCard}
	{#if canTriggerInput}
		<DeviceButtonV2
			id="devicebutton-bfjfyo9"
			aria-label={$format('trigger_input', { default: 'Trigger Input' })}
			size="l"
			variant="primary"
			isActive={pushType > PUSH_TYPE_RESET}
			disabled={!hasRights}
			on:click={handleClick}
			on:touchLongpress={handleTouchLongpress}
			on:touchstart={handleTouchStart}
		>
			{#if event}
				<IconForInputReader {event} color={'primary'} />
			{:else if buttonEvents?.length > 0 || rotationData}
				<RemoteControlVisualizer
					{buttonEvents}
					{rotationData}
					rotaryOnly={isBluRotaryKnob(device?.type)}
				/>
			{:else}
				<Icon class="width-full text-color-3" icon="fas fa-hand-back-point-up" size="s" />
			{/if}
		</DeviceButtonV2>
	{:else if buttonEvents?.length > 0 || rotationData || isBluRotaryKnob(device?.type)}
		<RemoteControlVisualizer
			{buttonEvents}
			{rotationData}
			rotaryOnly={isBluRotaryKnob(device?.type)}
		/>
	{:else if multiple_events?.length > 1}
		<DoubleIconForInputReader event={multiple_events} />
	{:else}
		<ParameterCardV2
			icon={mapIconToThin('fas fa-hand-back-point-up')}
			title={mappedEventTexts[event] || 'N/A'}
			size="m"
			backgroundColor="--color-0"
		>
			{#if event}
				<IconForInputReader {event} color={inputColor} />
			{/if}
		</ParameterCardV2>
	{/if}
{:else if canTriggerInput}
	<Stack gap="xs" align="center" direction="row">
		<DeviceButtonV2
			aria-label={$format('trigger_input', { default: 'Trigger Input' })}
			id="devicebutton-qzw6nhb"
			size="l"
			variant="primary"
			isActive={pushType > PUSH_TYPE_RESET}
			disabled={!hasRights}
			on:click={handleClick}
			on:touchLongpress={handleTouchLongpress}
			on:touchstart={handleTouchStart}
		>
			{#if event}
				<IconForInputReader {event} color={'primary'} />
			{/if}
			{#if buttonEvents?.length > 0 || rotationData}
				<RemoteControlVisualizer
					{buttonEvents}
					{rotationData}
					rotaryOnly={isBluRotaryKnob(device?.type)}
				/>
			{:else}
				<Icon class="width-full text-color-3" icon="fas fa-hand-back-point-up" size="s" />
			{/if}
		</DeviceButtonV2>
		<Text styleType={event ? 'tiny' : 'h1'} class="text-center">
			{mappedEventTexts[event] || 'N/A'}
		</Text>
	</Stack>
{:else if buttonEvents?.length > 0 || rotationData}
	<RemoteControlVisualizer {buttonEvents} {rotationData} rotaryOnly={isBluRotaryKnob(device?.type)} />
{:else if typeof multiple_events !== 'undefined'}
	<DoubleIconForInputReader event={multiple_events} />
{:else if event}
	<ControlsContainer color="color-0">
		<svelte:fragment slot="first">
			<Stack gap="xs">
				<IconForInputReader {event} color={inputColor} />
				<Icon class="width-full" icon="fat fa-hand-back-point-up" size="l" />
				<Text styleType={event ? 'body' : 'h1'}>
					{mappedEventTexts[event] || 'N/A'}
				</Text>
			</Stack>
		</svelte:fragment>
	</ControlsContainer>
{:else}
	<!-- N/A parameter should use ParameterCardV2 with size M and thin icon -->
	<ParameterCardV2
		icon={mapIconToThin('fas fa-hand-back-point-up')}
		title="N/A"
		description={$format('no_data_about_latest_report', {
			default: 'No data about the latest report.',
		})}
		size="m"
		backgroundColor="--color-0"
	/>
{/if}
